import React, { createContext, useState, useEffect, useContext } from 'react';
import { initialLeads, initialEvents, initialNotes, initialActivities } from '../utils/data';
import { UsersContext } from './UsersContext';

export const CRMContext = createContext();

export const CRMProvider = ({ children }) => {
  const { currentUser } = useContext(UsersContext);

  const [leads, setLeads] = useState(() => JSON.parse(localStorage.getItem('crm_leads')) || initialLeads);
  const [events, setEvents] = useState(() => JSON.parse(localStorage.getItem('crm_events')) || initialEvents);
  const [notes, setNotes] = useState(() => JSON.parse(localStorage.getItem('crm_notes')) || initialNotes);
  const [activities, setActivities] = useState(() => JSON.parse(localStorage.getItem('crm_activities')) || initialActivities);

  useEffect(() => {
    localStorage.setItem('crm_leads', JSON.stringify(leads));
    localStorage.setItem('crm_events', JSON.stringify(events));
    localStorage.setItem('crm_notes', JSON.stringify(notes));
    localStorage.setItem('crm_activities', JSON.stringify(activities));
  }, [leads, events, notes, activities]);

  const actor = () => ({
    actorId: currentUser?.id || null,
    actorName: currentUser?.name || 'System',
  });

  const logActivity = (leadId, action, description) => {
    const newActivity = {
      id: Date.now().toString() + Math.random(),
      leadId,
      action,
      description,
      createdAt: new Date().toISOString(),
      ...actor(),
    };
    setActivities(prev => [newActivity, ...prev]);
  };

  const addLead = (leadData) => {
    const id = Date.now().toString();
    const newLead = { ...leadData, id, createdAt: new Date().toISOString() };
    setLeads(prev => [newLead, ...prev]);
    logActivity(id, 'Lead Created', `Added ${newLead.name} to the system`);
    if (newLead.source === 'Recommended') {
      if (newLead.referrerType === 'manual' && newLead.referrerManual) {
        logActivity(id, 'Manual Referrer Saved', `Referred by ${newLead.referrerManual}`);
      } else if (newLead.referrerType === 'linked' && newLead.referrerLeadId) {
        const referrer = leads.find(l => l.id === newLead.referrerLeadId);
        logActivity(id, 'Linked Referrer Saved', `Referred by ${referrer?.name || 'an existing lead'}`);
        logActivity(newLead.referrerLeadId, 'Referral Created', `Referred ${newLead.name}`);
      }
    }
  };

  const updateLead = (id, updates) => {
    const oldLead = leads.find(l => l.id === id);
    setLeads(prev => prev.map(l => l.id === id ? { ...l, ...updates } : l));
    logActivity(id, 'Lead Edited', 'Updated lead details');
    if (updates.phones && updates.phones.length > (oldLead.phones?.length || 0)) {
      logActivity(id, 'Phone Added', 'Added a new contact number');
    }
    if (updates.emails && updates.emails.length > (oldLead.emails?.length || 0)) {
      logActivity(id, 'Email Added', 'Added a new email address');
    }
    if (updates.source && updates.source !== oldLead.source) {
      logActivity(id, 'Source Changed', `Source updated to ${updates.source}`);
    }
    if (updates.referrerLeadId && updates.referrerLeadId !== oldLead.referrerLeadId) {
      const referrer = leads.find(l => l.id === updates.referrerLeadId);
      logActivity(id, 'Referral Linked', `Referred by ${referrer?.name}`);
      logActivity(updates.referrerLeadId, 'Referral Created', `Referred ${oldLead.name}`);
    }
  };

  const addEvent = (event) => {
    const newEvent = { ...event, id: Date.now().toString(), createdAt: new Date().toISOString() };
    setEvents(prev => [...prev, newEvent]);
    const typeLabel = newEvent.type === 'task' ? 'Task' : 'Appointment';
    logActivity(newEvent.leadId, `${typeLabel} Created`, `Created ${newEvent.type}: ${newEvent.title}`);
  };

  const updateEvent = (id, updates) => {
    const evt = events.find(e => e.id === id);
    setEvents(prev => prev.map(e => e.id === id ? { ...e, ...updates } : e));
    if (!evt) return;
    const typeLabel = evt.type === 'task' ? 'Task' : 'Appointment';
    if (updates.status === 'completed' && evt.status !== 'completed') {
      logActivity(evt.leadId, `${typeLabel} Completed`, `Completed ${evt.type}: ${evt.title}`);
    } else if (updates.date && updates.date !== evt.date) {
      logActivity(evt.leadId, `${typeLabel} Rescheduled`, `Rescheduled ${evt.type}: ${evt.title}`);
    } else {
      logActivity(evt.leadId, `${typeLabel} Edited`, `Updated ${evt.type}: ${evt.title}`);
    }
  };

  const addNote = (leadId, content) => {
    const newNote = {
      id: Date.now().toString(),
      leadId,
      content,
      createdAt: new Date().toISOString(),
      ...actor(),
    };
    setNotes(prev => [newNote, ...prev]);
    logActivity(leadId, 'Note Added', `Added a note`);
  };

  const getLeadNextFollowUp = (leadId) => {
    const pendingEvents = events
      .filter(e => e.leadId === leadId && e.status === 'pending')
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    return pendingEvents.length > 0 ? pendingEvents[0].date : null;
  };

  return (
    <CRMContext.Provider value={{ leads, events, notes, activities, addLead, updateLead, addEvent, updateEvent, addNote, getLeadNextFollowUp }}>
      {children}
    </CRMContext.Provider>
  );
};